import { useState } from 'react';
import {
    Loader2,
    Users,
    MapPin,
    Phone,
    Mail,
    UserCheck,
    ArrowLeftRight,
    AlertTriangle,
    Check,
    Clock,
    X,
    ShieldCheck,
} from 'lucide-react';
import {
    useGetInvestigationGuarantorsQuery,
    useGetAvailableAgentsForGuarantorQuery,
    useReassignGuarantorMutation,
    useCsReassignGuarantorMutation,
    useUpdateGuarantorVerificationMutation,
} from '@/store/api/apiSlice';
import { GuarantorStatus, MEANS_OF_IDENTIFICATION_OPTIONS } from '@/types';
import type { InvestigationGuarantor } from '@/types';

// Which reassign endpoint the panel targets. Customer Success now owns guarantor
// reassignment (§6) and posts to its own scoped route; the legacy 'investigation'
// variant is kept for super-admins on the investigation detail page.
type ReassignVariant = 'investigation' | 'customer-success';

interface Props {
    investigationId: string;
    // Show the verification controls (status + notes). Agent (assigned) or admin.
    canManage: boolean;
    // Show the reassign control. CS-owned now — agents no longer reassign.
    canReassign?: boolean;
    reassignVariant?: ReassignVariant;
    // Advisory flag from the investigation: none of the guarantors are in-town.
    guarantorTownWarning?: boolean | undefined;
    employmentTown?: string | null | undefined;
    // Show the internal calling-hours rule. Staff only (agent / CS / admin) — the
    // people who actually place the calls. Not surfaced to employers.
    showCallWindow?: boolean | undefined;
}

const STATUS_LABEL: Record<GuarantorStatus, string> = {
    [GuarantorStatus.PENDING]: 'Pending',
    [GuarantorStatus.IN_PROGRESS]: 'In progress',
    [GuarantorStatus.VERIFIED]: 'Verified',
    [GuarantorStatus.FAILED]: 'Failed',
};

const STATUS_STYLE: Record<GuarantorStatus, string> = {
    [GuarantorStatus.PENDING]: 'bg-[#f2f2f2] text-[#4e4d4d]',
    [GuarantorStatus.IN_PROGRESS]: 'bg-[#FFF4E5] text-[#B26A00]',
    [GuarantorStatus.VERIFIED]: 'bg-[#E8F5E9] text-[#2E7D32]',
    [GuarantorStatus.FAILED]: 'bg-[#FDECEA] text-[#C62828]',
};

const STATUS_OPTIONS: GuarantorStatus[] = [
    GuarantorStatus.PENDING,
    GuarantorStatus.IN_PROGRESS,
    GuarantorStatus.VERIFIED,
    GuarantorStatus.FAILED,
];

function readErrorMessage(err: any, fallback = 'Something went wrong'): string {
    const msg = err?.data?.message ?? err?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    return msg ?? fallback;
}

function formatWhen(value?: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

// The guarantor's own recommendation, collected via the public link we email/SMS them.
// Three states: submitted · requested but awaiting · never contacted (no email/phone,
// or a guarantor created before this feature existed).
function GuarantorRecommendationBlock({ guarantor }: { guarantor: InvestigationGuarantor }) {
    const submitted = Boolean(guarantor.recommendationSubmittedAt);
    const requested = Boolean(guarantor.recommendationRequestedAt);

    if (submitted) {
        const confirmed = guarantor.relationshipConfirmed === true;
        return (
            <div className="mt-4 pt-4 border-t border-[#f0f0f0]">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[#9a9a9a]">
                        Guarantor recommendation
                    </span>
                    <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                            confirmed ? 'bg-[#E8F5E9] text-[#2E7D32]' : 'bg-[#FDECEA] text-[#C62828]'
                        }`}
                    >
                        {confirmed ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                        {confirmed ? 'Relationship confirmed' : 'Relationship denied'}
                    </span>
                </div>
                {guarantor.recommendation && (
                    <p className="whitespace-pre-wrap rounded-lg bg-[#fafafa] px-3 py-2.5 text-xs leading-relaxed text-[#1b1b1b]">
                        {guarantor.recommendation}
                    </p>
                )}
                <p className="mt-1.5 text-[11px] text-[#9a9a9a]">
                    Submitted {formatWhen(guarantor.recommendationSubmittedAt)}
                </p>
            </div>
        );
    }

    if (requested) {
        return (
            <div className="mt-4 pt-4 border-t border-[#f0f0f0]">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#9a9a9a]">
                    Guarantor recommendation
                </span>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-[#B26A00]">
                    <Clock className="w-3.5 h-3.5" />
                    Requested {formatWhen(guarantor.recommendationRequestedAt)} — awaiting response
                </p>
            </div>
        );
    }

    return (
        <div className="mt-4 pt-4 border-t border-[#f0f0f0]">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#9a9a9a]">
                Guarantor recommendation
            </span>
            <p className="mt-1 text-xs text-[#9a9a9a]">
                Not contacted — no email or phone was provided for this guarantor.
            </p>
        </div>
    );
}

/**
 * The signed indemnity form.
 *
 * Branches on `indemnity.submittedAt` — set only when all three acceptances were
 * given and the form was signed; `null` means it hasn't been completed. The
 * details collapse behind a toggle because `ipAddress`/`userAgent` are evidence
 * of execution rather than everyday reading.
 */
function GuarantorIndemnityBlock({ guarantor }: { guarantor: InvestigationGuarantor }) {
    const [open, setOpen] = useState(false);
    const indemnity = guarantor.indemnity;
    if (!indemnity?.submittedAt) return null;

    const idLabel =
        MEANS_OF_IDENTIFICATION_OPTIONS.find((o) => o.value === indemnity.meansOfIdentification)?.label ??
        indemnity.meansOfIdentification ??
        '—';

    return (
        <div className="mt-4 border-t border-[#f0f0f0] pt-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-[11px] font-semibold tracking-wider text-[#9a9a9a] uppercase">
                    Indemnity form
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F5E9] px-2.5 py-1 text-[11px] font-medium text-[#2E7D32]">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Signed {formatWhen(indemnity.submittedAt)}
                </span>
            </div>

            <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-2">
                {[
                    ['Occupation', indemnity.occupation],
                    ['Employer / business', indemnity.employerBusinessName],
                    ['Office address', indemnity.officeAddress],
                    ['Period known', indemnity.periodKnown],
                    ['Means of ID', idLabel],
                    ['ID number', indemnity.identificationNumber],
                    ['Signed as', indemnity.signatureName],
                    ['Form version', indemnity.formVersion],
                ].map(([label, value]) => (
                    <div key={label as string} className="flex gap-2">
                        <dt className="shrink-0 text-[#9a9a9a]">{label}:</dt>
                        <dd className="min-w-0 break-words text-[#1b1b1b]">{(value as string) || '—'}</dd>
                    </div>
                ))}
            </dl>

            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="mt-2.5 text-[11px] font-medium text-[#FF6B35] hover:underline"
            >
                {open ? 'Hide execution evidence' : 'Show execution evidence'}
            </button>
            {open && (
                <div className="mt-2 space-y-1 rounded-lg bg-[#fafafa] px-3 py-2.5 text-[11px] text-[#6b6b6b]">
                    <p>Indemnity accepted: {indemnity.indemnityAccepted ? 'Yes' : 'No'}</p>
                    <p>Verification consent accepted: {indemnity.verificationConsentAccepted ? 'Yes' : 'No'}</p>
                    <p>Declaration accepted: {indemnity.declarationAccepted ? 'Yes' : 'No'}</p>
                    <p className="break-all">IP address: {indemnity.ipAddress || '—'}</p>
                    <p className="break-all">User agent: {indemnity.userAgent || '—'}</p>
                </div>
            )}
        </div>
    );
}

// Calling-hours policy for guarantor verification calls. 9am–7pm is the normal
// window; 7pm–9pm is permitted only where necessary. Outside 9am–9pm, don't call.
function CallWindowNotice() {
    const hour = new Date().getHours();
    const inWindow = hour >= 9 && hour < 19; // 9:00am – 6:59pm
    const isLateWindow = hour >= 19 && hour < 21; // 7:00pm – 8:59pm

    const tone = inWindow
        ? 'border-[#e0e0e0] bg-[#fafafa] text-[#4e4d4d]'
        : isLateWindow
          ? 'border-[#FF6B35]/30 bg-[#FFF8F4] text-[#8a5a3b]'
          : 'border-red-200 bg-red-50 text-red-700';

    return (
        <div className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-xs ${tone}`}>
            <Clock className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="leading-relaxed">
                <span className="font-semibold">Call guarantors between 9:00am and 7:00pm</span> — extend to 9:00pm only
                where necessary.
                {!inWindow &&
                    (isLateWindow
                        ? ' It is now past 7:00pm, so only call if it is genuinely necessary.'
                        : ' It is currently outside calling hours. Please wait until 9:00am.')}
            </div>
        </div>
    );
}

export default function GuarantorsPanel({
    investigationId,
    canManage,
    canReassign = false,
    reassignVariant = 'investigation',
    guarantorTownWarning,
    employmentTown,
    showCallWindow = false,
}: Props) {
    const { data: guarantors = [], isLoading } = useGetInvestigationGuarantorsQuery(investigationId);

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 text-[#FF6B35] animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h3 className="text-sm font-bold text-[#1b1b1b]">Guarantors</h3>
                <p className="text-xs text-[#4e4d4d] mt-0.5">
                    Guarantors provided by the employee for verification.
                    {employmentTown ? ` Employment town: ${employmentTown}.` : ''}
                </p>
            </div>

            {showCallWindow && guarantors.length > 0 && <CallWindowNotice />}

            {guarantorTownWarning && (
                <div className="flex items-start gap-2 bg-[#FFF8F4] border border-[#FF6B35]/30 rounded-lg px-4 py-3 text-xs text-[#8a5a3b]">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-[#FF6B35]" />
                    <span>
                        None of the guarantors are in the employment town. An out-of-town guarantor can be reassigned to
                        an in-town agent below.
                    </span>
                </div>
            )}

            <div className="flex flex-col gap-3">
                {guarantors.length === 0 ? (
                    <div className="bg-[#fafafa] border border-dashed border-[#e0e0e0] rounded-xl py-10 text-center">
                        <Users className="w-6 h-6 text-[#4e4d4d] mx-auto mb-2" />
                        <p className="text-sm font-medium text-[#1b1b1b]">No guarantors submitted yet</p>
                        <p className="text-xs text-[#4e4d4d] mt-1">
                            The employee has not provided their guarantors for this investigation yet.
                        </p>
                    </div>
                ) : (
                    guarantors.map((g) => (
                        <GuarantorCard
                            key={g.id}
                            guarantor={g}
                            investigationId={investigationId}
                            canManage={canManage}
                            canReassign={canReassign}
                            reassignVariant={reassignVariant}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// Guarantor card
// ──────────────────────────────────────────────────────────────────────────────

function GuarantorCard({
    guarantor,
    investigationId,
    canManage,
    canReassign,
    reassignVariant,
}: {
    guarantor: InvestigationGuarantor;
    investigationId: string;
    canManage: boolean;
    canReassign: boolean;
    reassignVariant: ReassignVariant;
}) {
    const [showReassign, setShowReassign] = useState(false);
    const [status, setStatus] = useState<GuarantorStatus>(guarantor.status);
    const [notes, setNotes] = useState(guarantor.verificationNotes ?? '');
    const [saveError, setSaveError] = useState('');
    const [saved, setSaved] = useState(false);

    const [updateVerification, { isLoading: isSaving }] = useUpdateGuarantorVerificationMutation();

    const dirty = status !== guarantor.status || (notes ?? '') !== (guarantor.verificationNotes ?? '');

    const handleSave = async () => {
        setSaveError('');
        setSaved(false);
        try {
            await updateVerification({
                id: investigationId,
                guarantorId: guarantor.id,
                data: { status, verificationNotes: notes },
            }).unwrap();
            setSaved(true);
        } catch (err: any) {
            setSaveError(readErrorMessage(err, 'Failed to update verification.'));
        }
    };

    const assignedAgentName = guarantor.assignedAgent
        ? `${guarantor.assignedAgent.firstName} ${guarantor.assignedAgent.lastName}`.trim()
        : null;

    return (
        <div className="bg-white border border-[#e0e0e0] rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1b1b1b]">{guarantor.fullName}</p>
                    {guarantor.relationship && (
                        <p className="text-xs text-[#4e4d4d] mt-0.5">{guarantor.relationship}</p>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${STATUS_STYLE[guarantor.status]}`}
                    >
                        {STATUS_LABEL[guarantor.status]}
                    </span>
                    <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${
                            guarantor.withinEmploymentTown
                                ? 'bg-[#E8F5E9] text-[#2E7D32]'
                                : 'bg-[#FFF4E5] text-[#B26A00]'
                        }`}
                    >
                        {guarantor.withinEmploymentTown ? 'In-town' : 'Out-of-town'}
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-[#4e4d4d]">
                {guarantor.phone && (
                    <span className="flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-[#9a9a9a]" />
                        {guarantor.phone}
                    </span>
                )}
                {guarantor.email && (
                    <span className="flex items-center gap-1.5 min-w-0">
                        <Mail className="w-3.5 h-3.5 shrink-0 text-[#9a9a9a]" />
                        <span className="truncate">{guarantor.email}</span>
                    </span>
                )}
                {(guarantor.town || guarantor.state) && (
                    <span className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-[#9a9a9a]" />
                        {[guarantor.town, guarantor.state].filter(Boolean).join(', ')}
                    </span>
                )}
                {guarantor.address && (
                    <span className="flex items-center gap-1.5 sm:col-span-2">
                        <MapPin className="w-3.5 h-3.5 text-[#9a9a9a]" />
                        {guarantor.address}
                    </span>
                )}
                <span className="flex items-center gap-1.5 sm:col-span-2">
                    <UserCheck className="w-3.5 h-3.5 text-[#9a9a9a]" />
                    {assignedAgentName ? (
                        <>
                            Assigned to {assignedAgentName}
                            {guarantor.reassigned && <span className="text-[#B26A00]"> (reassigned)</span>}
                        </>
                    ) : (
                        'Not yet assigned'
                    )}
                </span>
            </div>

            <GuarantorRecommendationBlock guarantor={guarantor} />
            <GuarantorIndemnityBlock guarantor={guarantor} />

            {(canManage || canReassign) && (
                <div className="mt-4 pt-4 border-t border-[#f0f0f0] flex flex-col gap-3">
                    {canManage && (
                        <>
                            <div className="flex flex-col gap-2">
                                <label className="text-[11px] font-medium text-[#1b1b1b]">Verification status</label>
                                <select
                                    value={status}
                                    onChange={(e) => {
                                        setStatus(e.target.value as GuarantorStatus);
                                        setSaved(false);
                                    }}
                                    className="w-full border border-[#e0e0e0] rounded-lg px-3 py-2 text-sm text-[#1b1b1b] focus:outline-none focus:border-[#FF6B35]"
                                >
                                    {STATUS_OPTIONS.map((s) => (
                                        <option key={s} value={s}>
                                            {STATUS_LABEL[s]}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-[11px] font-medium text-[#1b1b1b]">Verification notes</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => {
                                        setNotes(e.target.value);
                                        setSaved(false);
                                    }}
                                    rows={2}
                                    placeholder="e.g. Contacted and confirmed identity & relationship."
                                    className="w-full border border-[#e0e0e0] rounded-lg px-3 py-2 text-sm text-[#1b1b1b] focus:outline-none focus:border-[#FF6B35] resize-none"
                                />
                            </div>

                            {saveError && <p className="text-xs text-red-600">{saveError}</p>}
                        </>
                    )}

                    <div className="flex items-center gap-2 flex-wrap">
                        {canManage && (
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={!dirty || isSaving}
                                className="bg-[#FF6B35] hover:bg-[#FF5722] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                            >
                                {isSaving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : saved && !dirty ? (
                                    <Check className="w-4 h-4" />
                                ) : null}
                                {saved && !dirty ? 'Saved' : 'Save verification'}
                            </button>
                        )}
                        {canReassign && (
                            <button
                                type="button"
                                onClick={() => setShowReassign((v) => !v)}
                                className="border border-[#e0e0e0] hover:bg-[#fafafa] text-[#1b1b1b] px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                            >
                                <ArrowLeftRight className="w-4 h-4" />
                                {showReassign ? 'Cancel' : 'Reassign'}
                            </button>
                        )}
                    </div>

                    {canReassign && showReassign && (
                        <ReassignForm
                            investigationId={investigationId}
                            guarantor={guarantor}
                            variant={reassignVariant}
                            onDone={() => setShowReassign(false)}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// Reassign form
// ──────────────────────────────────────────────────────────────────────────────

function ReassignForm({
    investigationId,
    guarantor,
    variant,
    onDone,
}: {
    investigationId: string;
    guarantor: InvestigationGuarantor;
    variant: ReassignVariant;
    onDone: () => void;
}) {
    const { data: agents = [], isLoading } = useGetAvailableAgentsForGuarantorQuery({
        id: investigationId,
        guarantorId: guarantor.id,
    });
    const [reassignInvestigation, { isLoading: isReassigningInvestigation }] = useReassignGuarantorMutation();
    const [reassignCs, { isLoading: isReassigningCs }] = useCsReassignGuarantorMutation();
    const isReassigning = isReassigningInvestigation || isReassigningCs;
    const [selectedAgentId, setSelectedAgentId] = useState('');
    const [error, setError] = useState('');

    const handleReassign = async () => {
        if (!selectedAgentId) return;
        setError('');
        try {
            const args = { id: investigationId, guarantorId: guarantor.id, agentId: selectedAgentId };
            await (variant === 'customer-success' ? reassignCs(args) : reassignInvestigation(args)).unwrap();
            onDone();
        } catch (err: any) {
            setError(readErrorMessage(err, 'Failed to reassign guarantor.'));
        }
    };

    return (
        <div className="bg-[#fafafa] border border-[#e0e0e0] rounded-lg p-3 flex flex-col gap-2">
            <p className="text-[11px] text-[#4e4d4d]">
                Agents in {guarantor.town || 'the guarantor’s town'} who can vet this guarantor:
            </p>
            {isLoading ? (
                <div className="flex justify-center py-4">
                    <Loader2 className="w-4 h-4 text-[#FF6B35] animate-spin" />
                </div>
            ) : agents.length === 0 ? (
                <p className="text-xs text-[#4e4d4d] py-2">
                    No in-town agents are available for this guarantor right now.
                </p>
            ) : (
                <>
                    <select
                        value={selectedAgentId}
                        onChange={(e) => setSelectedAgentId(e.target.value)}
                        className="w-full border border-[#e0e0e0] rounded-lg px-3 py-2 text-sm text-[#1b1b1b] bg-white focus:outline-none focus:border-[#FF6B35]"
                    >
                        <option value="">Select an agent…</option>
                        {agents.map((a) => (
                            <option key={a.id} value={a.id}>
                                {a.firstName} {a.lastName} — {a.city}
                            </option>
                        ))}
                    </select>
                    {error && <p className="text-xs text-red-600">{error}</p>}
                    <button
                        type="button"
                        onClick={handleReassign}
                        disabled={!selectedAgentId || isReassigning}
                        className="self-start bg-[#1b1b1b] hover:bg-[#000] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                        {isReassigning && <Loader2 className="w-4 h-4 animate-spin" />}
                        Confirm reassignment
                    </button>
                </>
            )}
        </div>
    );
}
